import PublicLayout from "@/components/layout/PublicLayout";
import { useState } from "react";
import { Link } from "react-router-dom";
import { recoverAccount } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Search, Plus, X, ArrowLeft } from "lucide-react";

export default function RecoverAccount() {
    const [emails, setEmails] = useState([""]);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const updateEmail = (i, val) => {
        const next = [...emails];
        next[i] = val;
        setEmails(next);
    };
    const addRow = () => {
        if (emails.length < 5) setEmails([...emails, ""]);
    };
    const removeRow = (i) => {
        if (emails.length === 1) return;
        setEmails(emails.filter((_, idx) => idx !== i));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleaned = emails
            .map((x) => x.trim().toLowerCase())
            .filter((x) => x && x.includes("@"));
        if (!cleaned.length) return;
        setLoading(true);
        try {
            await recoverAccount(cleaned);
            track("account_recovery_requested", { count: cleaned.length });
        } catch {
            // intentional silence — preserve non-disclosure
        } finally {
            setSubmitted(true);
            setLoading(false);
        }
    };

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div
                    className="bl-container py-16 max-w-md"
                    data-testid="recover-account-page"
                >
                    <Link
                        to="/auth/login"
                        className="inline-flex items-center gap-2 text-sm text-bl-muted hover:text-bl-primary mb-6"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to sign in
                    </Link>
                    <div className="bl-eyebrow">Account recovery</div>
                    <h1 className="mt-4 font-display font-semibold text-3xl sm:text-4xl tracking-tight text-bl leading-tight">
                        Recover your{" "}
                        <span className="font-italic-accent text-bl-primary">
                            account
                        </span>
                        .
                    </h1>
                    <p className="mt-4 text-bl-muted text-sm leading-relaxed">
                        If you're not sure which email you used, enter any
                        addresses you may have used. We'll quietly check each
                        one — for privacy, we won't tell you on-screen which
                        match.
                    </p>

                    {submitted ? (
                        <div
                            className="mt-8 bl-card p-7 text-center"
                            data-testid="recover-success-card"
                        >
                            <div className="mx-auto w-12 h-12 rounded-full bg-bl-primary/10 flex items-center justify-center">
                                <Search className="w-6 h-6 text-bl-primary" />
                            </div>
                            <h2 className="mt-4 text-xl font-display font-semibold text-bl">
                                We're checking.
                            </h2>
                            <p className="mt-3 text-sm text-bl-muted leading-relaxed">
                                If any of those emails are linked to an
                                account, we'll send recovery instructions
                                shortly.
                            </p>
                            <p className="mt-4 text-xs text-bl-muted">
                                Still stuck?{" "}
                                <Link
                                    to="/contact"
                                    className="text-bl-primary hover:underline"
                                >
                                    Contact support
                                </Link>
                                .
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            className="mt-8 bl-card p-6 md:p-7 space-y-4"
                        >
                            <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                Possible emails (up to 5)
                            </label>
                            {emails.map((val, i) => (
                                <div
                                    key={i}
                                    className="flex items-center gap-2"
                                >
                                    <input
                                        type="email"
                                        autoComplete="email"
                                        placeholder="you@example.com"
                                        value={val}
                                        onChange={(e) =>
                                            updateEmail(i, e.target.value)
                                        }
                                        className="flex-1 px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                        data-testid={`recover-email-input-${i}`}
                                    />
                                    {emails.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRow(i)}
                                            className="p-2 rounded-md hover:bg-bl-surface text-bl-muted"
                                            data-testid={`recover-remove-${i}`}
                                            aria-label="Remove email"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {emails.length < 5 && (
                                <button
                                    type="button"
                                    onClick={addRow}
                                    className="inline-flex items-center gap-2 text-sm text-bl-primary hover:underline"
                                    data-testid="recover-add-email"
                                >
                                    <Plus className="w-4 h-4" /> Add another
                                    email
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className="bl-btn-primary w-full"
                                data-testid="recover-submit-btn"
                            >
                                {loading ? "Checking…" : "Help me recover"}
                            </button>
                            <p className="text-center text-xs text-bl-muted pt-2">
                                Or{" "}
                                <Link
                                    to="/contact"
                                    className="text-bl-primary hover:underline"
                                >
                                    contact support
                                </Link>{" "}
                                if you need a human.
                            </p>
                        </form>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
